/**
 * AIResponseDecider - AI 回應決策系統
 * 負責決定 AI 是否回應，以及選擇哪張牌回應
 */

import type { GameState, CardDefinition, Card } from '../../types';
import type { ResponseOption, ResponseTriggerType } from '../../types/response';

// ============================================================
// 類型定義
// ============================================================

export interface AIDecision {
  shouldRespond: boolean;
  response: ResponseOption | null;
  targets: string[] | null;
  confidence: number; // 0-1，用於調整思考時間
  reasoning?: string; // Debug 用
}

export interface AIPersonality {
  /** 攻擊傾向 0-1 */
  aggression: number;
  /** 反應敏感度 0-1（越高越容易回應） */
  reactivity: number;
  /** 資源保守度 0-1（越高越惜牌） */
  conservation: number;
  /** 隨機性 0-1（增加不可預測性） */
  randomness: number;
}

// ============================================================
// 預設個性
// ============================================================

export const AI_PERSONALITIES = {
  aggressive: {
    aggression: 0.8,
    reactivity: 0.6,
    conservation: 0.2,
    randomness: 0.2,
  },
  defensive: {
    aggression: 0.3,
    reactivity: 0.8,
    conservation: 0.6,
    randomness: 0.2,
  },
  balanced: {
    aggression: 0.5,
    reactivity: 0.5,
    conservation: 0.5,
    randomness: 0.3,
  },
  chaotic: {
    aggression: 0.5,
    reactivity: 0.5,
    conservation: 0.3,
    randomness: 0.7,
  },
} as const;

// ============================================================
// AIResponseDecider
// ============================================================

export class AIResponseDecider {
  private personality: AIPersonality;
  private cardDefinitions: Map<string, CardDefinition>;

  constructor(
    personality: AIPersonality = AI_PERSONALITIES.balanced,
    cardDefinitions?: Map<string, CardDefinition>
  ) {
    this.personality = personality;
    this.cardDefinitions = cardDefinitions ?? new Map<string, CardDefinition>();
  }

  /**
   * 設定卡牌定義
   */
  setCardDefinitions(definitions: Map<string, CardDefinition>): void {
    this.cardDefinitions = definitions;
  }

  /**
   * 設定 AI 個性
   */
  setPersonality(personality: AIPersonality): void {
    this.personality = personality;
  }

  /**
   * 決定是否回應
   */
  decide(
    gameState: GameState,
    aiPlayerId: string,
    triggerType: ResponseTriggerType,
    triggerCardId: string | null,
    availableResponses: ResponseOption[]
  ): AIDecision {
    // 沒有可用回應
    if (availableResponses.length === 0) {
      return {
        shouldRespond: false,
        response: null,
        targets: null,
        confidence: 1,
        reasoning: '沒有可用的回應牌',
      };
    }

    // 評估每個回應選項
    const evaluatedResponses = availableResponses.map(response => ({
      response,
      score: this.evaluateResponse(gameState, aiPlayerId, triggerType, triggerCardId, response),
    }));

    // 按分數排序
    evaluatedResponses.sort((a, b) => b.score - a.score);

    const bestResponse = evaluatedResponses[0];

    // 加入隨機性
    const randomFactor = Math.random() * this.personality.randomness;
    const adjustedScore = bestResponse.score + randomFactor - (this.personality.randomness / 2);

    // 決定是否回應（考慮反應敏感度和資源保守度）
    const responseThreshold = 0.3 + (1 - this.personality.reactivity) * 0.3 + this.personality.conservation * 0.2;

    const shouldRespond = adjustedScore > responseThreshold;

    if (!shouldRespond) {
      return {
        shouldRespond: false,
        response: null,
        targets: null,
        confidence: 1 - adjustedScore,
        reasoning: `分數 ${adjustedScore.toFixed(2)} 低於閾值 ${responseThreshold.toFixed(2)}`,
      };
    }

    // 選擇目標
    const targets = this.selectTargets(gameState, aiPlayerId, bestResponse.response);

    return {
      shouldRespond: true,
      response: bestResponse.response,
      targets,
      confidence: adjustedScore,
      reasoning: `選擇 ${bestResponse.response.cardName}，分數 ${adjustedScore.toFixed(2)}`,
    };
  }

  /**
   * 評估單個回應的價值
   */
  private evaluateResponse(
    gameState: GameState,
    aiPlayerId: string,
    triggerType: ResponseTriggerType,
    triggerCardId: string | null,
    response: ResponseOption
  ): number {
    let score = 0.5; // 基礎分數

    const effectText = response.effectText.toLowerCase();

    // 根據觸發類型調整評估
    if (triggerType === 'attack_declare') {
      // 進攻宣言時，防禦類回應更有價值
      if (effectText.includes('保護') || effectText.includes('防止') || effectText.includes('格擋')) {
        score += 0.15;
      }
    }

    // 1. 無效化類效果 - 高價值
    if (effectText.includes('無效') || effectText.includes('取消')) {
      score += 0.3;

      // 如果對手打出的是高威脅牌，更有價值
      if (triggerCardId) {
        const threatLevel = this.assessThreat(gameState, triggerCardId);
        score += threatLevel * 0.2;
      }
    }

    // 2. 破壞類效果
    if (effectText.includes('破壞') || effectText.includes('消滅')) {
      score += 0.25;
    }

    // 3. 抽牌類效果
    if (effectText.includes('抽') && effectText.includes('牌')) {
      score += 0.15;
    }

    // 4. buff 類效果
    if (effectText.includes('+') && effectText.includes('攻擊')) {
      score += 0.1 * this.personality.aggression;
    }

    // 5. 防禦類效果
    if (effectText.includes('保護') || effectText.includes('防止')) {
      score += 0.2 * (1 - this.personality.aggression);
    }

    // 6. 根據場面狀況調整
    const boardState = this.assessBoardState(gameState, aiPlayerId);

    if (boardState === 'losing') {
      // 落後時更願意使用資源
      score += 0.15;
    } else if (boardState === 'winning') {
      // 領先時更保守
      score -= 0.1 * this.personality.conservation;
    }

    // 7. 手牌數量考慮
    const aiPlayer = gameState.player1.id === aiPlayerId ? gameState.player1 : gameState.player2;
    const handSize = aiPlayer.hand.length;

    if (handSize <= 2) {
      // 手牌少，更惜牌
      score -= 0.15 * this.personality.conservation;
    } else if (handSize >= 5) {
      // 手牌多，更願意用
      score += 0.1;
    }

    // 確保分數在 0-1 範圍內
    return Math.max(0, Math.min(1, score));
  }

  /**
   * 評估威脅程度
   */
  private assessThreat(gameState: GameState, cardId: string): number {
    // 嘗試從戰場或手牌找到這張牌
    const card = this.findCard(gameState, cardId);
    if (!card) return 0.5;

    const definition = this.cardDefinitions.get(card.definitionId);
    if (!definition) return 0.5;

    let threat = 0.3;

    // 根據卡牌類型
    if (definition.type === 'spell') {
      threat += 0.2;
    }

    // 根據攻擊力
    const atk = definition.atk ?? 0;
    if (atk >= 5) threat += 0.3;
    else if (atk >= 3) threat += 0.2;
    else if (atk >= 1) threat += 0.1;

    // 根據效果文字
    const effectText = definition.effectText?.toLowerCase() ?? '';
    if (effectText.includes('破壞') || effectText.includes('消滅')) {
      threat += 0.2;
    }
    if (effectText.includes('抽')) {
      threat += 0.1;
    }

    return Math.min(1, threat);
  }

  /**
   * 評估場面狀態
   */
  private assessBoardState(
    gameState: GameState,
    aiPlayerId: string
  ): 'winning' | 'losing' | 'even' {
    const aiPlayer = gameState.player1.id === aiPlayerId ? gameState.player1 : gameState.player2;
    const opponent = gameState.player1.id === aiPlayerId ? gameState.player2 : gameState.player1;
    const aiSide = gameState.player1.id === aiPlayerId ? 'player1Side' : 'player2Side';
    const opponentSide = gameState.player1.id === aiPlayerId ? 'player2Side' : 'player1Side';

    // 計算場上單位數和總攻擊力
    let aiUnits = 0;
    let aiTotalAtk = 0;
    let opponentUnits = 0;
    let opponentTotalAtk = 0;

    const countSide = (side: typeof aiSide) => {
      const battleSide = gameState.battlefield[side];
      let units = 0;
      let totalAtk = 0;

      for (const slot of [...battleSide.frontLine, ...battleSide.backLine]) {
        if (slot.unit) {
          units++;
          const def = this.cardDefinitions.get(slot.unit.definitionId);
          totalAtk += (def?.atk ?? 0) + (slot.unit.atkModifier ?? 0);
        }
      }

      return { units, totalAtk };
    };

    const aiStats = countSide(aiSide);
    const opponentStats = countSide(opponentSide);

    aiUnits = aiStats.units;
    aiTotalAtk = aiStats.totalAtk;
    opponentUnits = opponentStats.units;
    opponentTotalAtk = opponentStats.totalAtk;

    // 加入手牌數量的考量
    const aiAdvantage = 
      (aiUnits - opponentUnits) * 2 + 
      (aiTotalAtk - opponentTotalAtk) + 
      (aiPlayer.hand.length - opponent.hand.length);

    if (aiAdvantage > 3) return 'winning';
    if (aiAdvantage < -3) return 'losing';
    return 'even';
  }

  /**
   * 選擇目標
   */
  private selectTargets(
    gameState: GameState,
    aiPlayerId: string,
    response: ResponseOption
  ): string[] | null {
    if (!response.requiresTarget) {
      return null;
    }

    // 根據效果文字決定目標
    const effectText = response.effectText.toLowerCase();
    const targets: string[] = [];

    // 破壞/消滅類 - 選擇對手最強的單位
    if (effectText.includes('破壞') || effectText.includes('消滅')) {
      const opponentSide = gameState.player1.id === aiPlayerId ? 'player2Side' : 'player1Side';
      const bestTarget = this.findStrongestUnit(gameState, opponentSide);
      if (bestTarget) targets.push(bestTarget);
    }

    // buff 類 - 選擇自己最強的單位
    if (effectText.includes('+') && effectText.includes('攻擊')) {
      const aiSide = gameState.player1.id === aiPlayerId ? 'player1Side' : 'player2Side';
      const bestTarget = this.findStrongestUnit(gameState, aiSide);
      if (bestTarget) targets.push(bestTarget);
    }

    return targets.length > 0 ? targets : null;
  }

  /**
   * 找場上最強的單位
   */
  private findStrongestUnit(
    gameState: GameState,
    side: 'player1Side' | 'player2Side'
  ): string | null {
    const battleSide = gameState.battlefield[side];
    let strongest: { id: string; atk: number } | null = null;

    for (const slot of [...battleSide.frontLine, ...battleSide.backLine]) {
      if (slot.unit) {
        const def = this.cardDefinitions.get(slot.unit.definitionId);
        const atk = (def?.atk ?? 0) + (slot.unit.atkModifier ?? 0);

        if (!strongest || atk > strongest.atk) {
          strongest = { id: slot.unit.id, atk };
        }
      }
    }

    return strongest?.id ?? null;
  }

  /**
   * 從遊戲狀態中找到卡牌
   */
  private findCard(gameState: GameState, cardId: string): Card | null {
    // 搜尋雙方手牌
    for (const player of [gameState.player1, gameState.player2]) {
      const card = player.hand.find(c => c.id === cardId);
      if (card) return card;
    }

    // 搜尋戰場
    for (const side of [gameState.battlefield.player1Side, gameState.battlefield.player2Side]) {
      for (const slot of [...side.frontLine, ...side.backLine]) {
        if (slot.unit?.id === cardId) return slot.unit;
      }
    }

    return null;
  }
}

/**
 * 建立 AI 回應決策器
 */
export function createAIResponseDecider(
  personality?: AIPersonality,
  cardDefinitions?: Map<string, CardDefinition>
): AIResponseDecider {
  return new AIResponseDecider(personality, cardDefinitions);
}
